import type { LessonConfig } from '../../engine/plant/types';

/** Lessons 8, 9 and 10: alarm management, interlocks and first-out, and startup. */
export const lessons: LessonConfig[] = [
  {
    id: 'L8',
    number: 8,
    title: 'Alarm management',
    summary: 'Acknowledge, shelve, and what to do when a hundred of them arrive at once.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'intro',
        title: 'What an alarm is for',
        body:
          'An alarm is not a status light. ISA-18.2 defines it as *an audible or visible means of ' +
          'indicating to the operator an equipment malfunction, process deviation, or abnormal ' +
          'condition requiring a **response***.\n\n' +
          'That last word is the test. If there is nothing you would do about it, it should not ' +
          'be an alarm. Most bad alarm systems fail that test a few thousand times a day.',
      },
      {
        id: 'raise',
        title: 'Raise one',
        body:
          'Open the **Sandbox** screen and inject **Coil outlet thermocouple drifting**.\n\n' +
          'Wait for the coil-outlet deviation alarm to come in. Notice three things: the banner ' +
          'flashes, the horn sounds a pattern specific to that priority, and the priority is ' +
          'marked by a *shape* as well as a colour — a diamond for emergency, a triangle for ' +
          'high, a square for medium, a circle for low. Colour is never the only signal.',
      },
      {
        id: 'silence',
        title: 'Silence is not acknowledge',
        check: { op: 'true' },
        body:
          'Press **Silence** (or the S key).\n\n' +
          'The horn stops. The alarm is still flashing and still listed as UNACK, because you ' +
          'have not told the system you have seen it — only that you have heard it. Those are ' +
          'different statements and a good alarm system keeps them separate.',
      },
      {
        id: 'ack',
        title: 'Acknowledge',
        body:
          'Now go to the **Alarms** screen, click the alarm and read the right-hand panel: the ' +
          'likely cause, the consequence, and the corrective action. Every alarm in this game has ' +
          'all three, because an alarm without them is a puzzle rather than an instruction.\n\n' +
          'Press **Ack**. The state changes from UNACK to ACK and the flashing stops.',
      },
      {
        id: 'rtn',
        title: 'Return to normal',
        body:
          'Clear the fault in the Sandbox.\n\n' +
          'Watch the alarm disappear from the summary. Now do it the other way round: raise ' +
          'another alarm, clear it *before* acknowledging, and look at the state. It reads ' +
          '**RTN-UNACK** and it stays in the summary.\n\n' +
          'That is deliberate. An alarm that came and went while you were not looking is exactly ' +
          'the one you most need to know about.',
      },
      {
        id: 'shelve',
        title: 'Shelving',
        body:
          'Sometimes an alarm is real, known, and useless — a failed transmitter with a work ' +
          'order already raised, chattering every thirty seconds.\n\n' +
          'Press **Shelve**. You must give a reason and a duration; both go into the event log. ' +
          'The alarm stops annunciating and stops counting toward the alarm-rate metrics, and ' +
          'it un-shelves itself when the timer expires. Shelving is a controlled, temporary, ' +
          'audited action — which is precisely what distinguishes it from switching an alarm off.',
      },
      {
        id: 'flood',
        title: 'Floods',
        body:
          'Inject **Instrument air failure**.\n\n' +
          'Every valve in the plant strokes to its fail position at once and the alarms arrive in ' +
          'a wave. The Alarms screen shows the rate against the EEMUA 191 target of one per ten ' +
          'minutes; above ten in ten minutes it is formally a *flood*, and the shift report ' +
          'counts the minutes you spent in one.\n\n' +
          'The instinct in a flood is to start acknowledging. Resist it. Almost every alarm on ' +
          'the screen is a *consequence*; find the one that is a *cause*. The alarm summary is ' +
          'sorted by priority and then by time, and the oldest emergency-priority alarm is ' +
          'usually the one that started it.',
      },
    ],
  },

  {
    id: 'L9',
    number: 9,
    title: 'Interlocks, trips and first-out',
    summary: 'The layer of protection that does not ask your permission.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'intro',
        title: 'Not a controller',
        body:
          'An interlock is not a controller and it is not an alarm. It is a rule — *if this ' +
          'condition, then these actions* — that executes without asking, because by the time a ' +
          'human could react the damage is done.\n\n' +
          'Open the **Sandbox** screen and read the interlock cards. Each one states its cause, ' +
          'its effects, whether it latches, and what has to be true before you may reset it.',
      },
      {
        id: 'order',
        title: 'Protection acts last',
        body:
          'Inside every simulated scan, the order is: process, then measurement, then control, ' +
          'then protection.\n\n' +
          'Protection runs *after* the controllers, every scan, so a trip always wins over a ' +
          'controller trying to do the opposite. That ordering is not an implementation ' +
          'detail — it is the reason a trip is a trip.',
      },
      {
        id: 'trip',
        title: 'Trip the heater',
        check: { op: 'tripped', id: 'I-201' },
        body:
          'Inject **Crude charge pump trip**.\n\n' +
          'The charge flow collapses. A fired heater with no flow through the coil is the ' +
          'classic way to rupture a tube, so within ten seconds the interlock cuts the fuel.\n\n' +
          'Look at the interlock card. It reads **LATCHED** — the trip does not clear itself ' +
          'when the cause goes away, because someone has to look at the plant first.',
      },
      {
        id: 'firstout',
        title: 'First-out',
        body:
          'The card also names the **first-out** cause: *Crude charge flow low*.\n\n' +
          'This interlock has two causes — tube skin high-high and charge flow low — and after a ' +
          'trip both may well be true, because losing the flow makes the skin hot. First-out ' +
          'records which condition became true *first*, and it is often the only evidence of ' +
          'what actually happened.\n\n' +
          'On a real plant this is a dedicated hardware function, and shift teams have argued for ' +
          'days over trips that did not have it.',
      },
      {
        id: 'reset',
        title: 'Reset is a permissive, not a button',
        body:
          'Press **Reset** now. It is refused, and the refusal is written to the event log.\n\n' +
          'The reset permissive on this interlock requires the tube skin below 700 K *and* the ' +
          'charge flow back above 280 m³/h. A reset that works whenever you press it is not a ' +
          'protection function.\n\n' +
          'Clear the pump trip, let the flow come back, and reset it properly.',
      },
      {
        id: 'bypass',
        title: 'Bypass',
        body:
          'Some interlocks can be bypassed and some cannot. Try to bypass the heater trip: it ' +
          'refuses, because it is declared non-bypassable.\n\n' +
          'Try the column pressure trip instead. You are asked for a reason, it is logged with a ' +
          'timestamp, and the interlock card shows **BYPASSED** in amber for as long as it is ' +
          'defeated.\n\n' +
          'Bypassing a protective function is a real, permitted, audited operation — during ' +
          'commissioning, during a controlled shutdown, when the protection itself has failed. ' +
          'It is also how a great many people have been killed. The audit trail is the point.',
      },
    ],
  },

  {
    id: 'L10',
    number: 10,
    title: 'Startup sequencing',
    summary: 'Automating the order of operations, and why the order matters more than the steps.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'intro',
        title: 'Sequences',
        body:
          'A sequence controller is a state machine: a list of steps, each with entry actions, a ' +
          'hold time, and a condition that must be true before it moves on.\n\n' +
          'Open the **Sandbox** screen on the water treatment plant to see four of them — one per ' +
          'filter — or the reactor unit to see a controlled shutdown. Each card shows the current ' +
          'step, the progress through the sequence, and what it is waiting for.',
      },
      {
        id: 'permissive',
        title: 'Start permissives',
        body:
          'A sequence will not start unless its permissive is satisfied. The filter backwash ' +
          'sequence requires the clearwell above 40 per cent — a backwash draws about fifty cubic ' +
          'metres from it — and requires that no other filter is already being washed.\n\n' +
          'Try starting two backwashes at once. The second is refused and the refusal is logged. ' +
          'A permissive that can be clicked past is not a permissive.',
      },
      {
        id: 'order',
        title: 'The order is the point',
        body:
          'Watch a backwash run: isolate, drain, air scour, wash, settle, refill, filter to ' +
          'waste, return to service.\n\n' +
          'Every one of those steps exists because of something that went wrong once. Wash before ' +
          'draining and you blow media out of the box. Return to service before the ripening ' +
          'peak has passed and you push a turbidity spike straight into the clearwell — which is ' +
          'why the filter-to-waste step waits for the turbidity to fall below 0.15 NTU rather ' +
          'than simply timing out.',
      },
      {
        id: 'conflict',
        title: 'When a sequence fights a trip',
        body:
          'The reactor unit’s controlled shutdown deliberately empties the vessel, which takes ' +
          'the level straight through the low-low trip.\n\n' +
          'The sequence therefore bypasses that trip on its first step, with a logged reason, and ' +
          'removes the bypass when it completes or aborts. That is exactly how it is done under a ' +
          'permit on a real plant — and it is worth noticing that the *sequence* records why, so ' +
          'the bypass cannot be forgotten.',
      },
      {
        id: 'coldstart',
        title: 'Now do one by hand',
        body:
          'The hardest thing on any unit is a cold start, because none of the measurements mean ' +
          'anything yet and every loop has to be brought in one at a time.\n\n' +
          'Take the **Cold start** scenario on the water treatment plant, or the one on the ' +
          'reactor unit if you want the harder version. Fill from the front, get each loop stable ' +
          'in manual before you put it in automatic, and do not put a cascade secondary into CAS ' +
          'until its primary is settled.\n\n' +
          'That is the whole discipline: one thing at a time, in the right order, and wait for ' +
          'each one to answer.',
      },
    ],
  },
];
